import useHasPermissions from '@/components/access-control/useHasPermissions.hook';
import LinearLoading from '@/components/linear-loading';
import { useSnackbar } from '@/contexts/SnackBarContext';
import { FeatureLevelPermissions } from '@/models/UserPermissions.model';
import { ProgramReenrollPage } from '@/routes/ops/investments/ProgramReenrollPage.component';
import InvestmentService from '@/services/Investment.service';
import {
  PostProgramChangeReenrollmentBody,
  ProgramService
} from '@/services/ops/investments/Program.service';
import { PlanService } from '@/services/Plan.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCallback } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

type ProgramReenrollRouteParams = {
  newProgramId: string;
  planId: string;
};

const ProgramReenrollRoute = (): JSX.Element => {
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { planId, newProgramId } = useParams<ProgramReenrollRouteParams>();

  const hasSubmitPermission = useHasPermissions({
    requires: [FeatureLevelPermissions.WRITE_INVESTMENTS_MODIFY]
  });

  const planQuery = useQuery(
    ['InvestmentService.getPlanFunds', 'plan', planId],
    () => InvestmentService.getPlanFunds(+planId, +newProgramId),
    { cacheTime: 0, enabled: !!planId }
  );

  const plansInfo = useQuery(
    ['PlanService.getPlanById', planId],
    () => PlanService.getPlanById(planId),
    { enabled: !!planId }
  );

  const postPartialReenrollment = useMutation(
    ['ProgramService.postProgramChangeReenrollment', +newProgramId, +planId],
    ({
      effectiveDate,
      jiraTicket,
      mappings
    }: Omit<PostProgramChangeReenrollmentBody, 'reenrollmentType'>) => {
      return ProgramService.postProgramChangeReenrollment(
        +newProgramId,
        +planId,
        {
          effectiveDate,
          jiraTicket,
          mappings,
          reenrollmentType: 'partial'
        }
      );
    },
    {
      onError: (error: { message: string }) => {
        snackbar.showSnackbar({
          message: `Couldn’t submit changes to plan id ${planId}. ${error.message}`,
          severity: 'error'
        });
      },
      onSettled: () => {
        navigate(`/ops/investments/program/${newProgramId}`);
      },
      onSuccess: async () => {
        snackbar.showSnackbar({
          message: `Successfully submitted changes to plan id ${planId}`,
          severity: 'success'
        });
        queryClient.refetchQueries([
          'ProgramService.getProgramChangesWithApprovals',
          +newProgramId
        ]);
        await planQuery.refetch();
      }
    }
  );

  const onSubmit = useCallback(
    async ({
      jiraTicket,
      effectiveDate,
      mappings
    }: Omit<PostProgramChangeReenrollmentBody, 'reenrollmentType'>) => {
      postPartialReenrollment.mutate({ effectiveDate, jiraTicket, mappings });
    },
    [planId, newProgramId]
  );

  if (planQuery.isLoading || plansInfo.isLoading) return <LinearLoading />;

  if (hasSubmitPermission.isAllowed) {
    return (
      <ProgramReenrollPage
        currentProgram={planQuery.data?.currentProgram}
        name={planQuery.data.name}
        newProgram={planQuery.data?.newProgram}
        onSubmit={onSubmit}
        planId={+planId}
      />
    );
  } else {
    return <Navigate to={{ pathname: `/` }} />;
  }
};

export default ProgramReenrollRoute;
